"use client";
import React, { useState, useEffect } from "react";
import {
  useGetGradeSystemQuery,
  useUpdateGradeMutation,
} from "../hooks/useAdmin";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import {
  FaEdit,
  FaSave,
  FaTrash,
  FaPlus,
  FaGraduationCap,
  FaAward,
} from "react-icons/fa";
import SmallModal from "../components/SmallModal";
import loader from "../assets/loader.gif";

function GradingSystem() {
  const { data: gradez, isLoading } = useGetGradeSystemQuery();
  const gradingData = gradez?.data?.[0];
  const updateGrade = useUpdateGradeMutation();

  const [editing, setEditing] = useState(false);
  const [grades, setGrades] = useState<any[]>([]);
  const [traits, setTraits] = useState<any[]>([]);

  useEffect(() => {
    if (gradingData && gradez) {
      const parsedGrades =
        typeof gradingData?.grading_scale === "string"
          ? JSON.parse(gradingData.grading_scale)
          : gradingData.grading_scale || [];

      const parsedTraits =
        typeof gradingData?.extracurricular_traits === "string"
          ? JSON.parse(gradingData.extracurricular_traits)
          : gradingData.extracurricular_traits || [];

      setGrades(parsedGrades);
      setTraits(parsedTraits);
    }
  }, [gradingData, gradez]);

  // modal states
  const [openAddTraitModal, setOpenAddTraitModal] = useState(false);
  const [openAddGradeModal, setOpenAddGradeModal] = useState(false);

  const [newTrait, setNewTrait] = useState({ name: "" });
  const [newGrade, setNewGrade] = useState({
    min: "" as any,
    max: "" as any,
    letter: "",
    desc: "",
  });

  // === Handlers ===
  const handleGradeChange = (index: number, field: string, value: string) => {
    setGrades((prev) => {
      const updated = [...prev];
      // Keep as string if it's empty so the user can clear the input completely
      (updated[index] as any)[field] = value === "" ? "" : Number(value);
      return updated;
    });
  };

  const handleTraitChange = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setTraits((prev) => {
      const updated = [...prev];
      (updated[index] as any)[field] = value;
      return updated;
    });
  };

  const handleRemoveGrade = (index: number) => {
    setGrades((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleRemoveTrait = (index: number) => {
    setTraits((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleAddTrait = () => {
    if (!newTrait.name.trim()) return;
    setTraits((prev) => [...prev, { ...newTrait, max_rating: 5 }]);
    setNewTrait({ name: "" });
    setOpenAddTraitModal(false);
  };

  const handleAddGrade = () => {
    if (!newGrade.letter.trim()) return;
    // Fallback empty inputs to 0 on submission
    const sanitizedGrade = {
      ...newGrade,
      min: newGrade.min === "" ? 0 : Number(newGrade.min),
      max: newGrade.max === "" ? 0 : Number(newGrade.max),
    };
    setGrades((prev) => [...prev, sanitizedGrade]);
    setNewGrade({ min: "", max: "", letter: "", desc: "" });
    setOpenAddGradeModal(false);
  };

  const toggleEdit = () => {
    if (editing) {
      // Sanitize any remaining empty fields to 0 before sending to server
      const sanitizedGrades = grades.map((g) => ({
        ...g,
        min: g.min === "" ? 0 : Number(g.min),
        max: g.max === "" ? 0 : Number(g.max),
      }));

      let payload = {
        grading_scale: sanitizedGrades,
        extracurricular_traits: traits,
      };
      updateGrade.mutate(
        { payload },
        {
          onSuccess: () => {
            setEditing(false);
          },
        },
      );
    } else {
      setEditing(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center m-4 h-screen bg-white">
        <img src={loader} alt="loader" className="w-16 h-16 object-contain" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-slate-50/50 p-6 space-y-6">
      {/* Header Panel Layout */}
      <div className="w-full flex items-center gap-2">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Grading & Metrics System" />
      </div>

      {/* Overview Description Header Card */}
      <div className="w-full">
        <RegisterCard
          title="System Performance Registry"
          details="Configure global parameters for academic scores and developmental tracking parameters"
        />
      </div>

      {/* Content Distribution Panels Grid System */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        {/* Left Column Section: Grading Scale Table Card */}
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-sm items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center">
                <FaGraduationCap className="text-base" />
              </div>
              <div>
                <h2 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                  Academic Grading Scale
                </h2>
                <p className="text-[11px] font-medium text-slate-400">
                  Define operational cut-offs and scale distributions
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {editing && (
                <button
                  type="button"
                  onClick={() => setOpenAddGradeModal(true)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-200/70 active:scale-95 transition-all"
                >
                  <FaPlus className="text-[10px]" /> Add Grade
                </button>
              )}
              <button
                type="button"
                onClick={toggleEdit}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-bold shadow-sm transition-all active:scale-95 ${
                  editing
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-[#0096D1] hover:bg-[#007ba8] text-white"
                }`}
              >
                {editing ? (
                  <>
                    <FaSave className="text-xs" /> Save Changes
                  </>
                ) : (
                  <>
                    <FaEdit className="text-xs" /> Adjust System
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full border-collapse text-left table-fixed min-w-[550px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                  <th className="py-4 px-5 w-[20%]">Min Bounds</th>
                  <th className="py-4 px-4 w-[20%]">Max Bounds</th>
                  <th className="py-4 px-4 w-[20%]">Letter Index</th>
                  <th className="py-4 px-4 w-[30%]">Status Descriptor</th>
                  {editing && (
                    <th className="py-4 px-4 w-[10%] text-center">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {grades && grades.length > 0 ? (
                  grades.map((grade, index) => (
                    <tr
                      key={index}
                      className="group bg-white hover:bg-slate-50/30 transition-colors text-[13px] font-semibold text-slate-600"
                    >
                      {/* Column: Min Score Input */}
                      <td className="py-2.5 px-5 font-mono">
                        {editing ? (
                          <input
                            type="number"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown")
                                e.preventDefault();
                            }}
                            value={grade.min}
                            onChange={(e) =>
                              handleGradeChange(index, "min", e.target.value)
                            }
                            className="w-full text-sm font-bold px-2.5 py-1.5 bg-slate-50/60 rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                          />
                        ) : (
                          <span className="text-slate-700 font-bold">
                            {grade.min}
                          </span>
                        )}
                      </td>

                      {/* Column: Max Score Input */}
                      <td className="py-2.5 px-4 font-mono">
                        {editing ? (
                          <input
                            type="number"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown")
                                e.preventDefault();
                            }}
                            value={grade.max}
                            onChange={(e) =>
                              handleGradeChange(index, "max", e.target.value)
                            }
                            className="w-full text-sm font-bold px-2.5 py-1.5 bg-slate-50/60 rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                          />
                        ) : (
                          <span className="text-slate-700 font-bold">
                            {grade.max}
                          </span>
                        )}
                      </td>

                      {/* Column: Letter Grade */}
                      <td className="py-2.5 px-4">
                        {editing ? (
                          <input
                            type="text"
                            value={grade.letter}
                            onChange={(e) =>
                              handleGradeChange(index, "letter", e.target.value)
                            }
                            className="w-full text-sm font-black uppercase px-2.5 py-1.5 bg-slate-50/60 rounded-lg border border-slate-200 text-[#0096D1] focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                          />
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md font-mono font-black bg-[#0096D1]/5 text-[#0096D1] text-xs">
                            {grade.letter}
                          </span>
                        )}
                      </td>

                      {/* Column: Description */}
                      <td className="py-2.5 px-4">
                        {editing ? (
                          <input
                            type="text"
                            value={grade.desc}
                            onChange={(e) =>
                              handleGradeChange(index, "desc", e.target.value)
                            }
                            className="w-full text-sm font-medium px-2.5 py-1.5 bg-slate-50/60 rounded-lg border border-slate-200 text-slate-700 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                          />
                        ) : (
                          <span className="text-slate-500 font-medium capitalize">
                            {grade.desc || "No Description"}
                          </span>
                        )}
                      </td>

                      {editing && (
                        <td className="py-2.5 px-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveGrade(index)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 active:scale-95 transition-all mx-auto"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={editing ? 5 : 4}
                      className="py-10 text-center text-slate-400 font-medium text-xs italic"
                    >
                      No dynamic grade parameters registered...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Column Section: Developmental Traits (Made roomier / less tight) */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#012063]/5 text-[#012063] flex items-center justify-center">
                <FaAward className="text-base" />
              </div>
              <div>
                <h3 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                  Developmental Traits
                </h3>
                <p className="text-[11px] font-medium text-slate-400">
                  Behavioral criteria vectors
                </p>
              </div>
            </div>

            {editing && (
              <button
                type="button"
                onClick={() => setOpenAddTraitModal(true)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-[#0096D1] text-white hover:bg-[#007ba8] shadow-sm active:scale-95 transition-all"
              >
                <FaPlus className="text-xs" />
              </button>
            )}
          </div>

          <div className="p-5">
            {traits && traits.length > 0 ? (
              <ul className="space-y-3 max-h-[420px] overflow-y-auto register-scroll pr-1">
                {traits.map((trait, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between gap-4 p-3.5 bg-slate-50/50 hover:bg-slate-50 rounded-xl border border-slate-100 transition-all"
                  >
                    {editing ? (
                      <>
                        <input
                          type="text"
                          value={trait.name}
                          onChange={(e) =>
                            handleTraitChange(idx, "name", e.target.value)
                          }
                          className="flex-1 text-sm font-bold px-3 py-2 bg-white rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:border-[#0096D1] transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveTrait(idx)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50 transition-all shrink-0"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-xs font-bold text-slate-700 capitalize tracking-tight leading-snug">
                          {trait.name}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded font-mono text-[10px] font-bold bg-slate-100 border border-slate-200 text-slate-500 shrink-0">
                          Max: {trait.max_rating || 5}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-8 text-center text-slate-400 font-medium text-xs italic">
                No behavioral criteria vectors registered...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals Overlays */}
      {/* 1. Add Trait Modal */}
      {openAddTraitModal && (
        <SmallModal
          title="Add Custom Metrics Vector"
          isOpen={openAddTraitModal}
          onClose={() => setOpenAddTraitModal(false)}
        >
          <div className="space-y-4 pt-2 text-left">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                Trait Metric Name
              </label>
              <input
                type="text"
                placeholder="e.g., Leadership, Attentiveness"
                value={newTrait.name}
                onChange={(e) =>
                  setNewTrait({ ...newTrait, name: e.target.value })
                }
                className="w-full text-sm font-semibold px-3 py-2.5 bg-slate-50 text-slate-800 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
              />
            </div>
            <button
              type="button"
              onClick={handleAddTrait}
              className="w-full inline-flex items-center justify-center rounded-xl px-4 py-2.5 bg-[#0096D1] hover:bg-[#007ba8] text-white text-xs font-bold shadow-md transform active:scale-95 transition-all"
            >
              Append Trait Vector
            </button>
          </div>
        </SmallModal>
      )}

      {/* 2. Add Grade Modal */}
      {openAddGradeModal && (
        <SmallModal
          title="Add New Scale Rule"
          isOpen={openAddGradeModal}
          onClose={() => setOpenAddGradeModal(false)}
        >
          <div className="space-y-4 pt-2 text-left">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Min Bounds
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={newGrade.min}
                  onWheel={(e) => e.currentTarget.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown")
                      e.preventDefault();
                  }}
                  onChange={(e) =>
                    setNewGrade({ ...newGrade, min: e.target.value })
                  }
                  className="w-full text-sm font-bold px-3 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Max Bounds
                </label>
                <input
                  type="number"
                  placeholder="100"
                  value={newGrade.max}
                  onWheel={(e) => e.currentTarget.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown")
                      e.preventDefault();
                  }}
                  onChange={(e) =>
                    setNewGrade({ ...newGrade, max: e.target.value })
                  }
                  className="w-full text-sm font-bold px-3 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                Letter Index Code
              </label>
              <input
                type="text"
                placeholder="e.g., A+"
                value={newGrade.letter}
                onChange={(e) =>
                  setNewGrade({ ...newGrade, letter: e.target.value })
                }
                className="w-full text-sm font-black uppercase px-3 py-2.5 bg-slate-50 text-[#0096D1] placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
                Status Descriptor
              </label>
              <input
                type="text"
                placeholder="e.g., Excellent performance"
                value={newGrade.desc}
                onChange={(e) =>
                  setNewGrade({ ...newGrade, desc: e.target.value })
                }
                className="w-full text-sm font-medium px-3 py-2.5 bg-slate-50 text-slate-700 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
              />
            </div>

            <button
              type="button"
              onClick={handleAddGrade}
              className="w-full inline-flex items-center justify-center rounded-xl px-4 py-2.5 bg-[#0096D1] hover:bg-[#007ba8] text-white text-xs font-bold shadow-md transform active:scale-95 transition-all"
            >
              Inject New Operational Rule
            </button>
          </div>
        </SmallModal>
      )}
    </div>
  );
}

export default GradingSystem;
